// PRD §2.1 — left panel: card with a sticky-style header (icon + label + count),
// a scrollable tree body, and per-row interactions handled by `ProTreeRow`.
// Add / move / delete buttons land in Slices 5–7.

import { useTranslation } from 'react-i18next';
import { GitBranch } from 'lucide-react';

import type { ProDomain } from '../types/pro-item-design.types';
import { countItems } from '../utils/numbering';
import { DomainRow } from './ProTreeRow';

interface ProTreePanelProps {
  tree: ProDomain[];
  isLoading: boolean;
  isError: boolean;
}

export function ProTreePanel({ tree, isLoading, isError }: ProTreePanelProps) {
  const { t } = useTranslation('pro-item-design');
  const itemCount = countItems(tree);

  return (
    <div
      className="card"
      style={{ width: '30%', minWidth: 300, display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <GitBranch size={16} color="var(--info)" />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{t('tree.title')}</span>
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            marginLeft: 'auto',
          }}
        >
          {tree.length > 0 && !isLoading
            ? t('tree.count', { domains: tree.length, items: itemCount })
            : null}
        </span>
      </div>
      <div
        role="tree"
        style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}
      >
        {isLoading ? (
          <div style={{ padding: 24, color: 'var(--muted-foreground)', fontSize: 13 }}>
            {t('tree.loading')}
          </div>
        ) : isError ? (
          <div style={{ padding: 24, color: 'var(--destructive)', fontSize: 13 }}>
            {t('tree.loadError')}
          </div>
        ) : tree.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--muted-foreground)', fontSize: 13 }}>
            {t('tree.empty')}
          </div>
        ) : (
          tree.map((d, di) => (
            <DomainRow key={d.id} domain={d} domainIndex={di} siblings={tree} />
          ))
        )}
      </div>
    </div>
  );
}
